import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  ReactFlow,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  reconnectEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "@/styles/liveDashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { App, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { pushToHistory } from "@/redux/slices/dashboardV2.slice";
import { setActiveDashboard } from "@/redux/slices/user.slice";

// Node & edge type registries
import nodeTypes from "../nodes/nodeTypes";
import edgeTypes from "../edges/edgeTypes";

// Component registry
import { getComponentMeta } from "../registry/componentRegistry";

// Widget helpers
import {
  generateWidgetsFromNodes,
  extractWidgetStates,
  filterOutWidgets,
  isWidgetNode,
  isWidgetEdge,
  shouldHaveWidget,
  createWidgetNode,
  createWidgetEdge,
  getWidgetNodeId,
} from "../widgets/widgetHelpers";

// Panels & modals
import LiveComponentSelectModal from "../modals/LiveComponentSelectModal";
import ComponentConfigModal from "../modals/ComponentConfigModal";
import CanvasZoomControls from "@/components/Common/CanvasZoomControls";

// Redux
import {
  initCanvas,
  setNodes as setReduxNodes,
  setEdges as setReduxEdges,
  setViewport as setReduxViewport,
  markClean,
  markDirty as markReduxDirty,
  resetSaveTrigger,
  setSaving,
  setShowComponentSelect as setReduxShowComponentSelect,
} from "@/redux/slices/liveDashboard.slice";

// API
import {
  useLazyGetLiveDashboardCanvasQuery,
  useUpdateLiveDashboardLayoutMutation,
} from "@/redux/api/liveDashboardApi";

import PropTypes from "prop-types";

/**
 * LiveDashboardCanvas – Main React Flow canvas for SCADA visualization.
 *
 * Integrates with backend APIs:
 * - GET /live-dashboard/:id/canvas → load
 * - PATCH /live-dashboard/:id/layout → full save
 * - POST /live-dashboard/:id/components → add node
 * - POST /live-dashboard/:id/edges → add edge
 * - PATCH /live-dashboard/:id/viewport → persist pan/zoom
 *
 * Widget system:
 * - Visualization widgets (gauge/graph/value) render as separate floating nodes
 * - Connected to parent via dashed WidgetConnector edge
 * - Widget positions persisted in parent node's visualizationWidget.position
 * - Widgets auto-generated on load and after component creation/update
 */
const LiveDashboardCanvas = ({ dashboardId, tabId, tab, setData, readOnly = false, onZoomChange, dashboard }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const { fitView, screenToFlowPosition, setViewport, zoomIn, zoomOut } = useReactFlow();
  const edgeReconnectSuccessful = useRef(true);
  const canvasWrapperRef = useRef(null);

  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 350 });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 350 });
  }, [zoomOut]);

  // Redux state
  const { viewport: savedViewport, background, saveTriggered, showComponentSelect } = useSelector(
    (state) => state.liveDashboard
  );

  // Local canvas state (managed by React Flow)
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // UI state
  const setShowComponentSelect = useCallback(
    (val) => {
      dispatch(setReduxShowComponentSelect(val));
    },
    [dispatch]
  );
  const [showComponentConfig, setShowComponentConfig] = useState(false);
  const [selectedComponentType, setSelectedComponentType] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [showGrid] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // API hooks
  const [fetchCanvas, { isFetching: isLoading }] = useLazyGetLiveDashboardCanvasQuery();
  const [saveLayout] = useUpdateLiveDashboardLayoutMutation();

  // Counter for generating unique nodeIds
  const nodeCounterRef = useRef({});
  const defaultViewportRef = useRef(null);

  // ── Initialize canvas ───────────────────────────────────────────
  useEffect(() => {
    if (!dashboardId) return;

    const loadCanvas = async () => {
      try {
        let canvasData;
        if (tab?.liveCanvas) {
          canvasData = tab.liveCanvas;
        } else {
          const result = await fetchCanvas({ id: dashboardId, tabId }, false).unwrap();
          canvasData = result?.data || result;
        }

        const rawNodes = canvasData?.nodes || [];
        const rawEdges = canvasData?.edges || [];

        const loadedNodes = rawNodes.map((n) => {
          if (isWidgetNode(n)) return n;
          const meta = getComponentMeta(n.type);
          const hasCustomSize = (n.width && n.width !== 80) || (n.style?.width && n.style.width !== 80);
          const defaultW = meta?.defaultWidth || 80;
          const defaultH = meta?.defaultHeight || 80;
          const width = hasCustomSize ? (n.width || n.style?.width) : defaultW;
          const height = hasCustomSize ? (n.height || n.style?.height) : defaultH;

          return {
            id: n.id || n.nodeId,
            type: n.type,
            position: n.position || { x: 0, y: 0 },
            width,
            height,
            style: {
              ...(n.style || {}),
              width,
              height,
            },
            data: n.data || {
              _id: n._id,
              title: n.title,
              deviceId: n.deviceId,
              parameterId: n.parameterId,
              config: n.config,
              styles: n.styles,
              rotation: n.rotation,
              visualizationWidget: n.visualizationWidget,
            },
          };
        });

        const loadedEdges = rawEdges.map((e) => {
          if (isWidgetEdge(e)) return e;
          return {
            id: e.id || e.edgeId,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            label: e.label,
            type: "pipe",
            data: e.data || {
              _id: e._id,
              style: e.style || { type: "pipe" },
            },
          };
        });

        dispatch(
          initCanvas({
            id: dashboardId,
            name: canvasData?.name || "",
            nodes: loadedNodes,
            edges: loadedEdges,
            viewport: canvasData?.viewport,
            background: canvasData?.background,
          })
        );

        // Generate widget nodes and edges from loaded component nodes
        const { widgetNodes, widgetEdges } = generateWidgetsFromNodes(loadedNodes);

        setNodes([...loadedNodes, ...widgetNodes]);
        setEdges([...loadedEdges, ...widgetEdges]);

        // Build node counter from existing nodes
        const counter = {};
        loadedNodes.forEach((n) => {
          const [type, num] = (n.id || "").split("-");
          if (type && num) {
            counter[type] = Math.max(counter[type] || 0, parseInt(num, 10));
          }
        });
        nodeCounterRef.current = counter;

        setIsInitialized(true);

        const hasSavedViewport =
          canvasData?.viewport &&
          (canvasData.viewport.x !== 0 || canvasData.viewport.y !== 0 || canvasData.viewport.zoom !== 1);

        if (hasSavedViewport) {
          defaultViewportRef.current = {
            x: canvasData.viewport.x,
            y: canvasData.viewport.y,
            zoom: canvasData.viewport.zoom,
          };
          setTimeout(() => {
            setViewport({
              x: canvasData.viewport.x,
              y: canvasData.viewport.y,
              zoom: canvasData.viewport.zoom,
            });
            if (onZoomChange) onZoomChange(canvasData.viewport.zoom);
          }, 100);
        } else {
          defaultViewportRef.current = { x: 0, y: 0, zoom: 1 };
          setTimeout(() => fitView({ duration: 300, padding: 0.15, maxZoom: 1 }), 100);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load canvas:", error);
        setIsInitialized(true);
        dispatch(initCanvas({ id: dashboardId, name: "", nodes: [], edges: [] }));
      }
    };

    loadCanvas();
  }, [dashboardId, tabId]);

  // ── Sync nodes/edges to Redux (for dirty flag) ──────────────────
  // Only sync real SCADA nodes, not widget nodes
  useEffect(() => {
    if (isInitialized) {
      const realNodes = nodes.filter((n) => !isWidgetNode(n));
      dispatch(setReduxNodes(realNodes));
    }
  }, [nodes]);

  useEffect(() => {
    if (isInitialized) {
      const realEdges = edges.filter((e) => !isWidgetEdge(e));
      dispatch(setReduxEdges(realEdges));
    }
  }, [edges]);

  // ── Regenerate widgets when nodes change (e.g., after config edit) ──
  const regenerateWidgets = useCallback((currentNodes, currentEdges) => {
    // Get only real SCADA nodes
    const realNodes = currentNodes.filter((n) => !isWidgetNode(n));
    const existingWidgets = currentNodes.filter((n) => isWidgetNode(n));

    // Generate what widgets should exist
    const { widgetNodes: newWidgetNodes, widgetEdges: newWidgetEdges } = generateWidgetsFromNodes(realNodes);

    // Merge: preserve existing widget positions if they already exist
    const mergedWidgets = newWidgetNodes.map((newWidget) => {
      const existing = existingWidgets.find((w) => w.id === newWidget.id);
      if (existing) {
        // Keep existing position and size but update data (in case config changed)
        return {
          ...newWidget,
          position: existing.position,
          width: existing.width || existing.measured?.width,
          height: existing.height || existing.measured?.height,
        };
      }
      return newWidget;
    });

    // Remove widget nodes/edges that should no longer exist
    const validWidgetIds = new Set(mergedWidgets.map((w) => w.id));

    // Build final node/edge arrays
    const finalNodes = [
      ...realNodes,
      ...mergedWidgets,
    ];

    const realEdges = currentEdges.filter((e) => !isWidgetEdge(e));
    const finalEdges = [
      ...realEdges,
      ...newWidgetEdges.filter((e) => validWidgetIds.has(e.target)),
    ];

    return { finalNodes, finalEdges };
  }, []);

  // ── Generate unique nodeId ──────────────────────────────────────
  const generateNodeId = useCallback((type) => {
    const current = nodeCounterRef.current[type] || 0;
    const next = current + 1;
    nodeCounterRef.current[type] = next;
    return `${type}-${next}`;
  }, []);

  // ── Helper to convert current state to storage format ───────────
  const getStorageCanvas = useCallback(
    (currentNodes, currentEdges, currentViewport) => {
      const targetNodes = currentNodes || nodes;
      const targetEdges = currentEdges || edges;
      const targetViewport = currentViewport || savedViewport;

      const widgetStates = extractWidgetStates(targetNodes);
      const { nodes: realNodes, edges: realEdges } = filterOutWidgets(targetNodes, targetEdges);

      const storageNodes = realNodes.map((n) => {
        const widgetState = widgetStates[n.id];
        return {
          nodeId: n.id,
          type: n.type,
          position: n.position,
          width: n.width || n.measured?.width,
          height: n.height || n.measured?.height,
          title: n.data?.title,
          deviceId: n.data?.deviceId || undefined,
          parameterId: n.data?.parameterId || undefined,
          config: {
            ...(n.data?.config || {}),
            nodeWidth: n.width || n.measured?.width,
            nodeHeight: n.height || n.measured?.height,
            widgetPosition: widgetState?.position || n.data?.config?.widgetPosition,
            widgetWidth: widgetState?.width || n.data?.config?.widgetWidth,
            widgetHeight: widgetState?.height || n.data?.config?.widgetHeight,
          },
          styles: n.data?.styles || {},
          rotation: n.data?.rotation || 0,
          visualizationWidget: widgetState
            ? {
                enabled: true,
                type: n.data?.config?.visualization || "value",
                position: widgetState.position,
                width: widgetState.width,
                height: widgetState.height,
              }
            : n.visualizationWidget || n.data?.visualizationWidget || undefined,
        };
      });

      const storageEdges = realEdges.map((e) => ({
        edgeId: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        label: e.label,
        style: e.data?.style || { type: "pipe" },
      }));

      return {
        nodes: storageNodes,
        edges: storageEdges,
        viewport: targetViewport || { x: 0, y: 0, zoom: 1 },
        background: background || {},
      };
    },
    [nodes, edges, savedViewport, background]
  );

  // ── Sync in-memory canvas state to parent data.tabs / data.liveCanvas ───
  const syncToParent = useCallback(
    (customNodes, customEdges, customViewport) => {
      if (readOnly || !setData) return;
      const updatedStorage = getStorageCanvas(customNodes, customEdges, customViewport);

      setData((prev) => {
        if (!prev) return prev;
        if (tabId && prev.tabs && Array.isArray(prev.tabs)) {
          const idx = prev.tabs.findIndex((t) => t.tabId === tabId || (t._id && t._id === tabId));
          if (idx === -1) return prev;
          const currentTabCanvas = prev.tabs[idx]?.liveCanvas;
          if (
            JSON.stringify(currentTabCanvas?.nodes) === JSON.stringify(updatedStorage.nodes) &&
            JSON.stringify(currentTabCanvas?.edges) === JSON.stringify(updatedStorage.edges) &&
            JSON.stringify(currentTabCanvas?.viewport) === JSON.stringify(updatedStorage.viewport)
          ) {
            return prev;
          }
          const nextTabs = [...prev.tabs];
          nextTabs[idx] = {
            ...nextTabs[idx],
            liveCanvas: updatedStorage,
          };
          return { ...prev, tabs: nextTabs };
        } else {
          const currentLiveCanvas = prev.liveCanvas;
          if (
            JSON.stringify(currentLiveCanvas?.nodes) === JSON.stringify(updatedStorage.nodes) &&
            JSON.stringify(currentLiveCanvas?.edges) === JSON.stringify(updatedStorage.edges) &&
            JSON.stringify(currentLiveCanvas?.viewport) === JSON.stringify(updatedStorage.viewport)
          ) {
            return prev;
          }
          return { ...prev, liveCanvas: updatedStorage };
        }
      });
    },
    [readOnly, setData, tabId, getStorageCanvas]
  );

  // ── Handle new connection ───────────────────────────────────────
  const onConnect = useCallback(
    (connection) => {
      if (readOnly) return;
      if (
        connection.source?.startsWith("widget-") ||
        connection.target?.startsWith("widget-")
      ) {
        return;
      }

      const edgeId = `edge-${Date.now()}`;
      const newEdge = {
        id: edgeId,
        ...connection,
        type: "pipe",
        data: { style: { type: "pipe", color: "#64748b" } },
      };

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [readOnly, setEdges]
  );

  // ── Handle edge reconnect ──────────────────────────────────────
  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      edgeReconnectSuccessful.current = true;
      setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
    },
    [setEdges]
  );

  const onReconnectEnd = useCallback(
    (_, edge) => {
      if (!edgeReconnectSuccessful.current) {
        setEdges((eds) => {
          const nextEds = eds.filter((e) => e.id !== edge.id);
          syncToParent(nodes, nextEds, savedViewport);
          return nextEds;
        });
      }
      edgeReconnectSuccessful.current = true;
    },
    [nodes, savedViewport, syncToParent, setEdges]
  );

  // ── Handle node deletion ────────────────────────────────────────
  const onNodesDelete = useCallback(
    (deletedNodes) => {
      const idsToDelete = new Set();
      for (const node of deletedNodes) {
        if (isWidgetNode(node)) continue;
        idsToDelete.add(node.id);
        idsToDelete.add(getWidgetNodeId(node.id));
      }

      setNodes((nds) => {
        const nextNodes = nds.filter((n) => !idsToDelete.has(n.id));
        setEdges((eds) => {
          const nextEdges = eds.filter(
            (e) =>
              !idsToDelete.has(e.source) &&
              !idsToDelete.has(e.target) &&
              !idsToDelete.has(e.id?.replace("widget-edge-", ""))
          );
          syncToParent(nextNodes, nextEdges, savedViewport);
          return nextEdges;
        });
        return nextNodes;
      });
    },
    [savedViewport, syncToParent, setNodes, setEdges]
  );

  // ── Handle edge deletion ────────────────────────────────────────
  const onEdgesDelete = useCallback(
    (deletedEdges) => {
      const edgeIdsToRemove = new Set(deletedEdges.filter((e) => !isWidgetEdge(e)).map((e) => e.id));
      if (edgeIdsToRemove.size > 0) {
        setEdges((eds) => {
          const nextEdges = eds.filter((e) => !edgeIdsToRemove.has(e.id));
          syncToParent(nodes, nextEdges, savedViewport);
          return nextEdges;
        });
      }
    },
    [nodes, savedViewport, syncToParent, setEdges]
  );

  // ── Handle node drag stop ──────────────────────────────────────
  const handleNodeDragStop = useCallback(() => {
    syncToParent(nodes, edges, savedViewport);
  }, [nodes, edges, savedViewport, syncToParent]);

  const lastReportedZoomRef = useRef(1);

  // Do not trigger parent re-renders during active zoom/pan animation frames
  const handleMove = useCallback(() => {}, []);

  // ── Handle viewport change ─────────────────────────────────────
  const onMoveEnd = useCallback(
    (event, viewport) => {
      if (onZoomChange && Math.abs(viewport.zoom - lastReportedZoomRef.current) > 0.001) {
        lastReportedZoomRef.current = viewport.zoom;
        onZoomChange(viewport.zoom);
      }
      if (readOnly) {
        return;
      }
      dispatch(setReduxViewport(viewport));
      syncToParent(nodes, edges, viewport);

      if (event) {
        dispatch(markReduxDirty());
      }
    },
    [dispatch, readOnly, nodes, edges, syncToParent, onZoomChange]
  );

  const handleResetToDefault = useCallback(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobile) {
      fitView({ padding: 0.05, maxZoom: 1, duration: 400 });
    } else {
      const target = defaultViewportRef.current || { x: 0, y: 0, zoom: 1 };
      setViewport(target, { duration: 400 });
    }
  }, [fitView, setViewport]);

  // ── Handle double-click on node (edit config) ───────────────────
  const onNodeDoubleClick = useCallback((_, node) => {
    if (isWidgetNode(node)) return;

    setEditingNode(node);
    setSelectedComponentType(node.type);
    setShowComponentConfig(true);
  }, []);

  // ── Component selection flow ────────────────────────────────────
  const handleComponentTypeSelect = useCallback((type) => {
    setSelectedComponentType(type);
    setEditingNode(null);
    setShowComponentConfig(true);
  }, []);

  // ── Add/update component on canvas ──────────────────────────────
  const handleComponentConfigSubmit = useCallback(
    (config) => {
      // ── EDIT existing node ──
      if (editingNode) {
        const updateBody = {
          title: config.title ?? editingNode.data?.title ?? "",
          config: {
            ...(editingNode.data?.config || {}),
            ...(config.config || {}),
          },
          styles: {
            ...(editingNode.data?.styles || {}),
            ...(config.styles || {}),
          },
          deviceId: config.deviceId !== undefined ? config.deviceId : editingNode.data?.deviceId,
          parameterId: config.parameterId !== undefined ? config.parameterId : editingNode.data?.parameterId,
        };

        setNodes((nds) => {
          const updatedNodes = nds.map((n) =>
            n.id === editingNode.id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    ...updateBody,
                  },
                }
              : n
          );

          const { finalNodes, finalEdges } = regenerateWidgets(updatedNodes, edges);
          setTimeout(() => setEdges(finalEdges), 0);
          syncToParent(finalNodes, finalEdges, savedViewport);
          return finalNodes;
        });

        setShowComponentConfig(false);
        setEditingNode(null);
        return;
      }

      // ── CREATE new node ──
      const type = selectedComponentType;
      const meta = getComponentMeta(type) || {};
      const nodeId = generateNodeId(type);

      let position = { x: 200, y: 150 };
      if (screenToFlowPosition) {
        position = screenToFlowPosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        });
        position.x += (Math.random() - 0.5) * 40;
        position.y += (Math.random() - 0.5) * 40;
      }

      const nodeWidth = meta?.defaultWidth || 80;
      const nodeHeight = meta?.defaultHeight || 80;

      const optimisticNode = {
        id: nodeId,
        type,
        position,
        width: nodeWidth,
        height: nodeHeight,
        style: {
          width: nodeWidth,
          height: nodeHeight,
        },
        data: {
          title: config.title,
          config: { ...meta.defaultConfig, ...config.config },
          styles: { ...meta.defaultStyles, ...config.styles },
          deviceId: config.deviceId,
          parameterId: config.parameterId,
        },
      };

      setNodes((nds) => {
        const updated = [...nds, optimisticNode];
        if (shouldHaveWidget(optimisticNode)) {
          const widgetNode = createWidgetNode(optimisticNode);
          const widgetEdge = createWidgetEdge(nodeId);
          setTimeout(() => {
            setEdges((eds) => {
              const nextEds = [...eds, widgetEdge];
              syncToParent([...updated, widgetNode], nextEds, savedViewport);
              return nextEds;
            });
          }, 0);
          return [...updated, widgetNode];
        }
        syncToParent(updated, edges, savedViewport);
        return updated;
      });

      setShowComponentConfig(false);
    },
    [
      editingNode,
      selectedComponentType,
      generateNodeId,
      screenToFlowPosition,
      regenerateWidgets,
      edges,
      savedViewport,
      syncToParent,
      setNodes,
      setEdges,
    ]
  );

  // ── Save layout ────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    // ponytail: allow handleSave to execute when triggered even if readOnly transition is underway
    try {
      dispatch(setSaving(true));
      const storageCanvas = getStorageCanvas(nodes, edges, savedViewport);

      await saveLayout({
        id: dashboardId,
        tabId,
        body: {
          nodes: storageCanvas.nodes,
          edges: storageCanvas.edges,
          viewport: storageCanvas.viewport,
        },
      }).unwrap();

      dispatch(markClean());
      notification.success({
        key: "dashboard-save-notification",
        message: "Dashboard saved successfully",
        duration: 2,
        showProgress: true,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save layout:", error);
      notification.error({
        message: "Failed to save dashboard",
        description: error?.data?.message || "Please try again.",
      });
    } finally {
      dispatch(setSaving(false));
    }
  }, [nodes, edges, savedViewport, dashboardId, tabId, saveLayout, dispatch, getStorageCanvas, notification]);

  const syncToParentRef = useRef(syncToParent);
  useEffect(() => {
    syncToParentRef.current = syncToParent;
  }, [syncToParent]);

  // Sync to parent on unmount (when switching tabs in edit mode)
  useEffect(() => {
    return () => {
      if (!readOnly && syncToParentRef.current) {
        syncToParentRef.current();
      }
    };
  }, [readOnly]);

  // Debounced sync to parent on any nodes/edges/viewport change during editing
  useEffect(() => {
    if (!isInitialized || readOnly || !setData) return;
    const timer = setTimeout(() => {
      syncToParent(nodes, edges, savedViewport);
    }, 200);
    return () => clearTimeout(timer);
  }, [nodes, edges, savedViewport, isInitialized, readOnly, syncToParent, setData]);

  // Handle explicit save trigger (from parent when saving single-canvas live dashboard)
  useEffect(() => {
    if (saveTriggered) {
      handleSave().finally(() => {
        dispatch(resetSaveTrigger());
      });
    }
  }, [saveTriggered, handleSave, dispatch]);

  // ── Custom nodes change handler to prevent widget/connector deletion via keys ──
  const handleNodesChange = useCallback(
    (changes) => {
      // Filter out remove changes for widget nodes
      const filteredChanges = changes.filter((change) => {
        if (change.type === "remove") {
          // Check if this is a widget node
          return !change.id?.startsWith("widget-");
        }
        return true;
      });
      onNodesChange(filteredChanges);
    },
    [onNodesChange]
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      // Filter out remove changes for widget edges
      const filteredChanges = changes.filter((change) => {
        if (change.type === "remove") {
          return !change.id?.startsWith("widget-edge-");
        }
        return true;
      });
      onEdgesChange(filteredChanges);
    },
    [onEdgesChange]
  );

  // ── Node Click Linking Handler for View Mode ────────────────────
  const handleNodeClick = useCallback(
    (event, node) => {
      if (readOnly) {
        const config = node?.data?.config || {};
        const targetDashboardId = config.linkDashboardId || node?.data?.linkDashboardId;
        const targetTabId = config.linkTabId || node?.data?.linkTabId;
        if (targetDashboardId) {
          // ponytail: preserve tabId in history url and state for back-nav restoration
          const currentUrl =
            window.location.pathname + (tabId ? `?tabId=${tabId}` : window.location.search);
          dispatch(
            pushToHistory({
              url: currentUrl,
              activeDashboardId: dashboardId,
              activeTabId: tabId,
            })
          );

          dispatch(setActiveDashboard(targetDashboardId));
          let targetUrl = "/";
          if (targetTabId) {
            targetUrl += `?tabId=${targetTabId}`;
          }
          navigate(targetUrl);
        }
      }
    },
    [readOnly, dispatch, navigate, dashboardId, tabId]
  );

  // ── Inject readOnly into node/edge data ──────────────────────────
  const nodesWithReadOnly = useMemo(() =>
    nodes.map((n) => ({
      ...n,
      draggable: !readOnly,
      selectable: !readOnly,
      data: { ...n.data, readOnly },
    })),
    [nodes, readOnly]
  );

  const edgesWithReadOnly = useMemo(() =>
    edges.map((e) => ({
      ...e,
      data: { ...e.data, readOnly },
    })),
    [edges, readOnly]
  );

  // ── Memoize node/edge types ─────────────────────────────────────
  const memoizedNodeTypes = useMemo(() => nodeTypes, []);
  const memoizedEdgeTypes = useMemo(() => edgeTypes, []);

  return (
    <div className="live-dashboard-canvas-container">
      <Spin spinning={isLoading} size="large" tip="Loading dashboard...">
        <div ref={canvasWrapperRef} className="live-dashboard-canvas">
          <ReactFlow
            className={readOnly ? "read-only" : ""}
            nodes={nodesWithReadOnly}
            edges={edgesWithReadOnly}
            proOptions={{ hideAttribution: true }}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onMove={handleMove}
            onMoveEnd={onMoveEnd}
            nodeTypes={memoizedNodeTypes}
            edgeTypes={memoizedEdgeTypes}
            defaultViewport={savedViewport}
            minZoom={0.1}
            maxZoom={2.5}
            defaultEdgeOptions={{
              type: "pipe",
              data: { style: { type: "pipe", color: "#64748b" } },
            }}
            fitView={!isInitialized}
            fitViewOptions={{ padding: 0.1, maxZoom: 1 }}
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
            multiSelectionKeyCode="Shift"
            connectionLineStyle={{ stroke: "#64748b", strokeWidth: 3 }}
            connectionMode="loose"
            connectionRadius={35}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
            edgesReconnectable={!readOnly}
            edgesFocusable={!readOnly}
            panOnDrag={true}
            zoomOnScroll={false}
            zoomOnPinch={false}
            preventScrolling={false}
            selectionMode={readOnly ? "none" : "box"}
            onConnect={!readOnly ? onConnect : undefined}
            onNodesDelete={!readOnly ? onNodesDelete : undefined}
            onEdgesDelete={!readOnly ? onEdgesDelete : undefined}
            onNodeDoubleClick={!readOnly ? onNodeDoubleClick : undefined}
            onReconnectStart={!readOnly ? onReconnectStart : undefined}
            onReconnect={!readOnly ? onReconnect : undefined}
            onReconnectEnd={!readOnly ? onReconnectEnd : undefined}
            onNodeDragStop={!readOnly ? handleNodeDragStop : undefined}
            onNodeClick={handleNodeClick}
          >
            {/* Background grid */}
            {showGrid && (
              <Background
                variant={background?.variant || "dots"}
                color={background?.patternColor || "#e2e8f0"}
                gap={background?.gap || 20}
                size={background?.size || 1}
              />
            )}
          </ReactFlow>
          <CanvasZoomControls
            canvasRef={canvasWrapperRef}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleResetToDefault}
          />
        </div>
      </Spin>

      {/* Component selection modal */}
      <LiveComponentSelectModal
        show={showComponentSelect}
        onClose={() => setShowComponentSelect(false)}
        onSelect={handleComponentTypeSelect}
      />

      {/* Component configuration modal */}
      <ComponentConfigModal
        show={showComponentConfig}
        onClose={() => {
          setShowComponentConfig(false);
          setEditingNode(null);
        }}
        onBack={() => {
          setShowComponentConfig(false);
          setEditingNode(null);
          setShowComponentSelect(true);
        }}
        onSubmit={handleComponentConfigSubmit}
        mode={editingNode ? "edit" : "create"}
        componentType={selectedComponentType}
        initialData={editingNode?.data}
        dashboardData={dashboard || tab}
      />
    </div>
  );
};

LiveDashboardCanvas.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  tabId: PropTypes.string,
  tab: PropTypes.object,
  setData: PropTypes.func,
  readOnly: PropTypes.bool,
  onZoomChange: PropTypes.func,
  dashboard: PropTypes.object,
};

export default LiveDashboardCanvas;
